import { cn } from '@/lib/utils';
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/components/common/truncated-text';
import { formatDateToYYYYMMDD } from '@/utils/date-formatter';
import { IconButton } from '@/components/common/icon-button';
import { DeleteIcon, DisableIcon, EditIcon, EnableIcon, MoreIcon } from '@/components/agent-dashboard/assets';
import { MoreActionItem, MoreActionPopover } from '@/components/common/more-action-popover';
import { ConfirmModal } from '@/components/common/confirm-modal';
import { Pagination } from '@/components/common/pagination';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { agentPermissionHooks } from '@/manager';
import { PermissionUserData } from '@/services/agent-v2-list-agent-user';
import { CreateAdminModal } from './create-admin-modal';
import { useToast } from '@/hooks/use-toast';
import { CopyClipboard } from '../common/copy-clipboard';
import { ExpirationVerify } from '../agent-dashboard/expiration-verify';

interface TableListProps {
  permissionList: PermissionUserData[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  changePage: (page: number) => void;
  search: () => void;
}

/** 智能体权限管理-权限表格列表 */
export const PermissionTableList = ({
  permissionList,
  loading,
  total,
  page,
  pageSize,
  changePage,
  search,
}: TableListProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { loading: toggleLoading, toggleUserStatus } = agentPermissionHooks.useToggleUserStatus();
  const { loading: deleteLoading, deleteAdmin } = agentPermissionHooks.useDeleteAdmin();
  const { canManageRole } = agentPermissionHooks.useUserRole();

  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState<PermissionUserData | null>(null);

  const verifyRole = (roleType: number) => {
    if (!canManageRole(roleType)) {
      toast.default({
        description: t('agent_dashboard.permission.no_permission_tips'),
      })
      return false;
    }
    return true;
  }

  const handleEdit = (data: PermissionUserData) => {
    if (!verifyRole(data.roleType)) return;
    setShowEditModal(true);
    setSelected({ ...data });
  }

  const handleDelete = async (data: PermissionUserData) => {
    if (!verifyRole(data.roleType)) return;
    await deleteAdmin(data.userSubId, data.roleType);
  }

  const handleChangeStatus = async (data: PermissionUserData) => {
    if (!verifyRole(data.roleType)) return;
    await toggleUserStatus(data.userSubId, data.roleType);
  }

  const handleSizeChange = (page: number) => {
    changePage(page);
    search();
  }

  const roleMap = {
    '1': t('agent_dashboard.permission.table.role_map.operator'),
    '2': t('agent_dashboard.permission.table.role_map.admin'),
    '3': t('agent_dashboard.permission.table.role_map.super_admin'),
  }

  const permissionMap = {
    'user_management': t('agent_dashboard.permission.table.permission_map.user_management'),
    'permission_management': t('agent_dashboard.permission.table.permission_map.permission_management'),
    'data_overview_management': t('agent_dashboard.permission.table.permission_map.data_overview_management'),
    'content_management': t('agent_dashboard.permission.table.permission_map.content_management'),
    'order_management': t('agent_dashboard.permission.table.permission_map.order_management'),
    'company_management': t('agent_dashboard.permission.table.permission_map.company_management'),
  }

  const statusMap = {
    '1': t('agent_dashboard.permission.table.status_map.enabled'),
    '0': t('agent_dashboard.permission.table.status_map.disabled'),
  }

  const roleStatusMap = {
    '1': {
      label: t('agent_dashboard.permission.table.disable'),
      modalTitle: t('agent_dashboard.permission.table.disable_user'),
      modalDesc: t('agent_dashboard.permission.table.disable_user_desc'),
    },
    '0': {
      label: t('agent_dashboard.permission.table.enable'),
      modalTitle: t('agent_dashboard.permission.table.enable_user'),
      modalDesc: t('agent_dashboard.permission.table.enable_user_desc'),
    }
  }

  return (
    <div className='relative flex-1 flex flex-col rounded-lg border overflow-hidden'>
      <div
        className="overflow-auto flex-1"
      >
        <table className="w-full rounded-t-lg relative">
          <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
            <tr className="bg-gray-50">
              <Th>{t('agent_dashboard.permission.table.admin')}/{t('agent_dashboard.permission.table.id')}</Th>
              <Th>{t('agent_dashboard.permission.table.email')}</Th>
              <Th>{t('agent_dashboard.permission.table.role')}</Th>
              <Th>{t('agent_dashboard.permission.table.permission')}</Th>
              <Th>{t('agent_dashboard.permission.table.status')}</Th>
              <Th className='min-w-[7rem]'>{t('agent_dashboard.permission.table.create_time')}</Th>
              {/* <Th>{t('agent_dashboard.permission.table.last_login_time')}</Th> */}
              <Th className='sticky right-0 bg-gray-50 min-w-[7rem] max-w-28 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]'>{t('agent_dashboard.permission.table.action')}</Th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            {permissionList?.length > 0 && permissionList?.map((item, index) => (
              <tr key={`${item.id}_${index}`} className='border-b h-18 transition-colors duration-200'>
                <Td>
                  <TruncatedText text={item.userName} className='text-black pb-1' />
                  {item.id ? <div className='flex items-center'>
                    <TruncatedText text={item.id} className='mr-1' />
                    <CopyClipboard text={item.id} />
                  </div> : '-'}
                </Td>
                <Td>
                  <div className='flex items-center'>
                    <TruncatedText text={item.email} className='mr-1' />
                    <CopyClipboard text={item.email} />
                  </div>
                </Td>
                <Td className='min-w-[8rem]'>
                  <span className={cn(
                    "py-1 px-2 rounded-lg text-sm font-medium",
                    item.roleType === 1 && "text-[rgba(193,21,116,1)] bg-[rgba(253,242,250,1)]",
                    item.roleType === 2 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                    item.roleType === 3 && "text-[rgba(89,37,220,1)] bg-[rgba(244,243,255,1)]",
                  )}>{roleMap[item.roleType]}</span>
                </Td>
                <Td className='min-w-[22rem]'>
                  {item.permissions?.map((role) => (
                    <span key={role} className={cn(
                      "py-1 px-2 text-[#344054] font-medium rounded-md text-sm mr-1 bg-[#F2F4F7]",
                    )}>{permissionMap[role]}</span>
                  ))}
                </Td>
                <Td className='w-50'>
                  <span className={cn(
                    "py-1 px-2 rounded-lg text-xs font-medium",
                    item.roleStatus === 1 && "text-[#016630] bg-[#DCFCE7]",
                    item.roleStatus === 0 && "text-[#1E2939] bg-[#F3F4F6]",
                  )}>{statusMap[item.roleStatus]}</span>
                </Td>
                <Td className=''>{formatDateToYYYYMMDD(item.createdAt)}</Td>
                {/* <Td className='min-w-[11rem]'>{formatToFullDateTime(item.lastLoginAt)}</Td> */}
                <Td className='sticky right-0 bg-white min-w-[7rem] z-[5] group-hover:bg-gray-50 space-x-5 flex items-center py-4 h-full'>
                  <ExpirationVerify>
                    <IconButton
                      className='size-10 rounded-[8px]'
                      icon={<EditIcon className={cn('size-6', !canManageRole(item.roleType) && 'text-gray-400')} />}
                      onClick={() => handleEdit(item)}
                    />
                  </ExpirationVerify>
                  <MoreActionPopover
                    side='left'
                    className='w-40'
                    tiggerIcon={<IconButton className='size-10 rounded-[8px]' icon={<MoreIcon className='size-6' />} />}
                  >
                    <ConfirmModal
                      trigger={(
                        <ExpirationVerify className='w-full'>
                          <MoreActionItem
                            disabled={!canManageRole(item.roleType)}
                            icon={item.roleStatus === 1 ? (
                              <DisableIcon className={cn('size-5')} />
                            ) : <EnableIcon className={cn('size-5')} />
                            }
                            className={!canManageRole(item.roleType) && 'cursor-not-allowed'}
                            label={roleStatusMap[item.roleStatus].label}
                          />
                        </ExpirationVerify>
                      )}
                      title={roleStatusMap[item.roleStatus].modalTitle}
                      description={roleStatusMap[item.roleStatus].modalDesc}
                      cancel={t('agent_dashboard.confirm_modal.cancel')}
                      confirm={t('agent_dashboard.confirm_modal.confirm')}
                      onConfirm={async () => handleChangeStatus(item)}
                      loading={toggleLoading}
                    />
                    <ConfirmModal
                      trigger={(
                        <ExpirationVerify className='w-full'>
                          <MoreActionItem
                            disabled={!canManageRole(item.roleType)}
                            icon={<DeleteIcon className="w-5 h-5" />}
                            label={t('agent_dashboard.user_management.table.delete')}
                          />
                        </ExpirationVerify>
                      )}
                      title={t('agent_dashboard.confirm_modal.delete_title')}
                      description={t('agent_dashboard.confirm_modal.delete_desc')}
                      cancel={t('agent_dashboard.confirm_modal.cancel')}
                      confirm={t('agent_dashboard.confirm_modal.confirm')}
                      onConfirm={async () => handleDelete(item)}
                      loading={deleteLoading}
                    />
                  </MoreActionPopover>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='h-[4.25rem] border-t border-[rgba(234,236,240,1)] px-6 flex items-center'>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handleSizeChange}
        />
      </div>
      <CreateAdminModal
        type='update'
        open={showEditModal}
        onOpenChange={setShowEditModal}
        details={selected}
      />
      <LoadingOverlay loading={loading} />
    </div>
  )
}

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6  text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
)

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem]  pl-6 text-sm font-medium  text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
)